import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import type { WithNativeDataTestIdAttribute, WithOptionalDataTestId } from '../../types/qa';
import { cn } from '../../utils';

/**
 * Visual scale for height, padding, and type (aligned with `Button` `size` and similar to
 * [Radix Themes Text Field](https://www.radix-ui.com/themes/docs/components/text-field) sizes 1–3).
 *
 * Native HTML `input` `size` (character width) is not supported — use `className` (e.g. `max-w-*`) for width.
 */
const inputVariants = cva(
    'flex w-full rounded-md border border-input bg-input-background outline-none transition-[color,box-shadow] file:border-0 file:bg-transparent file:font-medium placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:bg-muted/50 disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
    {
        variants: {
            size: {
                /** Dense rows / toolbars — 28px. */
                xs: 'h-[28px] min-h-[28px] px-2 py-0 text-xs leading-tight file:h-[28px] file:text-xs',
                /** Compact — 32px. */
                sm: 'h-[32px] min-h-[32px] px-2.5 py-1 text-sm file:text-sm',
                /** Standard form control — 36px. */
                md: 'h-[36px] min-h-[36px] px-3 py-1.5 text-sm file:text-sm',
                default: 'h-[36px] min-h-[36px] px-3 py-1.5 text-sm file:text-sm',
                /** Comfortable — 48px. */
                lg: 'h-[48px] min-h-[48px] px-4 py-2.5 text-base file:text-base',
            },
        },
        defaultVariants: {
            size: 'default',
        },
    },
);

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & VariantProps<typeof inputVariants> & WithOptionalDataTestId;

export type InputSize = NonNullable<VariantProps<typeof inputVariants>['size']>;

/**
 * Heights in px used by {@link inputVariants}.
 * Used by non-Tailwind controls (e.g. react-select / `ReneoSelect`) so fields align with `Input`.
 *
 * | Size | px |
 * |------|----|
 * | `xs` | 28 |
 * | `sm` | 32 |
 * | `default` | 36 |
 * | `lg` | 48 |
 */
export const INPUT_CONTROL_HEIGHT_PX: Record<InputSize, number> = {
    xs: 28,
    sm: 32,
    md: 36,
    default: 36,
    lg: 48,
} as const;

/** @deprecated Use `INPUT_CONTROL_HEIGHT_PX` */
export const INPUT_CONTROL_MIN_HEIGHT_PX = INPUT_CONTROL_HEIGHT_PX;

/**
 * WebKit/Blink: `::-webkit-calendar-picker-indicator` sits with default inset-end padding — looks “floating”.
 * Absolutely position chip to inset-end + vertical center; asymmetric `pr-*` replaces symmetric `px-*` from variants (via tailwind-merge).
 */
const NATIVE_CALENDAR_INPUT_ALIGN: Record<InputSize, string> = {
    xs: 'relative pl-2 pr-8 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-1.5 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:-translate-y-1/2 [&::-webkit-calendar-picker-indicator]:m-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer',
    sm: 'relative pl-2.5 pr-9 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-2 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:-translate-y-1/2 [&::-webkit-calendar-picker-indicator]:m-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer',
    md: 'relative pl-3 pr-10 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-2 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:-translate-y-1/2 [&::-webkit-calendar-picker-indicator]:m-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer',
    default:
        'relative pl-3 pr-10 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-2 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:-translate-y-1/2 [&::-webkit-calendar-picker-indicator]:m-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer',
    lg: 'relative pl-4 pr-11 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:-translate-y-1/2 [&::-webkit-calendar-picker-indicator]:m-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer',
};

function isNativeCalendarPickerType(t: InputProps['type']): boolean {
    return t === 'date' || t === 'datetime-local';
}

function isVisuallyHiddenInputClass(className: InputProps['className']): boolean {
    return typeof className === 'string' && /\b(sr-only|hidden)\b/.test(className);
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(({ className, type, size, dataTestId, ...props }, ref) => {
    const { 'data-testid': nativeTestId, ...rest } = props as React.InputHTMLAttributes<HTMLInputElement> & WithNativeDataTestIdAttribute;
    const testId = dataTestId ?? nativeTestId;
    const resolvedSize = size ?? 'default';
    const isVisuallyHidden = isVisuallyHiddenInputClass(className);

    return (
        <input
            type={type}
            className={cn(
                !isVisuallyHidden && inputVariants({ size }),
                isNativeCalendarPickerType(type) && !isVisuallyHidden ? NATIVE_CALENDAR_INPUT_ALIGN[resolvedSize] : null,
                className,
            )}
            ref={ref}
            data-testid={testId}
            {...rest}
        />
    );
});
Input.displayName = 'Input';

export { Input };
